import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, CheckCircle2, AlertCircle, ExternalLink } from 'lucide-react';
import { reportApi } from '@/api/report.api';
import { usePermission } from '@/hooks/usePermission';
import { useAuth } from '@/hooks/useAuth';
import { Permissions } from '@/constants/permissions';
import { AppRoutes } from '@/constants/routes';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';
import { ReportToolbar, RunReportButton } from '@/components/reports/ReportToolbar';
import { LocationSelect } from '@/components/reports/LocationSelect';
import { CostCentreSelect } from '@/components/reports/CostCentreSelect';
import { FinancialPeriodSelect } from '@/components/reports/FinancialPeriodSelect';
import { ReportPrintPreview } from '@/components/reports/ReportPrintPreview';
import { ReportHeader } from '@/components/master/report/ReportHeader';
import { ReportFooter } from '@/components/master/report/ReportFooter';
import { exportToExcel } from '@/utils/reportExport';
import type { ExcelColumn } from '@/utils/reportExport';
import type { FinancialStatementDto, FinancialStatementFilter, FinancialStatementRow } from '@/types/report.types';

// ── helpers ────────────────────────────────────────────────────────────────────

function today() {
  return new Date().toISOString().split('T')[0];
}
function firstOfMonth() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
}
function fmtAmt(n: number) {
  if (n === 0) return '—';
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtDate(iso: string | null) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return iso ?? '—';
  }
}

const INPUT_CLS =
  'w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500';
const LABEL_CLS = 'block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1';

// ── Excel export ───────────────────────────────────────────────────────────────

const EXCEL_COLUMNS: ExcelColumn[] = [
  { key: 'Name', header: 'Description', width: 48 },
  { key: 'Amount', header: 'Amount', width: 16, numFmt: '#,##0.00' },
  { key: 'ComparativeAmount', header: 'Comparative Amount', width: 20, numFmt: '#,##0.00' },
  { key: 'Variance', header: 'Variance', width: 16, numFmt: '#,##0.00' },
  { key: 'VariancePct', header: 'Variance %', width: 12, numFmt: '#,##0.0' },
  { key: 'RowType', header: 'Row Type', width: 16 },
];

function buildExcelRows(rows: FinancialStatementRow[]) {
  return rows
    .filter(r => r.rowType !== 'SPACER')
    .map(r => ({
      Name: r.name,
      Amount: r.amount,
      ComparativeAmount: r.comparativeAmount ?? '',
      Variance: r.variance ?? '',
      VariancePct: r.variancePct ?? '',
      RowType: r.rowType,
    }));
}

// ── Row renderer ───────────────────────────────────────────────────────────────

interface FsRowProps {
  row: FinancialStatementRow;
  showComparative: boolean;
  monthly: boolean;
  cellHeaders: string[];
  colSpanTotal: number;
  onDrilldown?: (accountId: string) => void;
}

// Monthly mode (GAP-019): one figure per financial period, then the row's total. Rendered in place of
// the amount/comparative/variance columns. `strong` bolds subtotal and net rows.
function MonthlyCells({ row, cellHeaders, pad, strong }: {
  row: FinancialStatementRow; cellHeaders: string[]; pad: string; strong?: boolean;
}) {
  return (
    <>
      {cellHeaders.map((h, i) => {
        const v = row.cells?.[i] ?? 0;
        return (
          <td key={h} className={cn(pad, 'text-sm text-right tabular-nums', strong && 'font-semibold',
            v === 0 ? 'text-gray-400 dark:text-gray-500'
              : v < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-gray-300')}>
            {v !== 0 ? `${v < 0 ? '-' : ''}${fmtAmt(Math.abs(v))}` : '—'}
          </td>
        );
      })}
      <td className={cn(pad, 'text-sm text-right tabular-nums font-bold',
        row.isNegative ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white')}>
        {row.isNegative ? '-' : ''}{fmtAmt(Math.abs(row.amount))}
      </td>
    </>
  );
}

// Variance Δ and Δ% cells (GAP-019). Rendered only when a comparative column is shown; the padding
// is passed so each row type keeps its own row height.
function VarianceCells({ row, pad, bold }: { row: FinancialStatementRow; pad: string; bold?: boolean }) {
  const v = row.variance;
  return (
    <>
      <td className={cn(pad, 'text-sm text-right tabular-nums', bold && 'font-bold',
        v == null || v === 0 ? 'text-gray-400 dark:text-gray-500'
          : v > 0 ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400')}>
        {v == null || v === 0 ? '—' : `${v > 0 ? '+' : '−'}${fmtAmt(Math.abs(v))}`}
      </td>
      <td className={cn(pad, 'text-sm text-right tabular-nums text-gray-500 dark:text-gray-400', bold && 'font-bold')}>
        {row.variancePct != null ? `${row.variancePct.toFixed(1)}%` : '—'}
      </td>
    </>
  );
}

function FsRow({ row, showComparative, monthly, cellHeaders, colSpanTotal, onDrilldown }: FsRowProps) {
  const indent = row.indentLevel * 16;

  if (row.rowType === 'SPACER') {
    return <tr><td colSpan={colSpanTotal} className="py-2" /></tr>;
  }

  if (row.rowType === 'VALIDATION') {
    return (
      <tr>
        <td colSpan={colSpanTotal} className="px-4 py-2">
          <div className="flex items-center gap-2 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded-lg px-3 py-2">
            <AlertCircle size={13} />
            {row.name}
          </div>
        </td>
      </tr>
    );
  }

  if (row.rowType === 'SECTION_HEADER') {
    return (
      <tr className="bg-gray-100 dark:bg-gray-800">
        <td
          colSpan={colSpanTotal}
          className="px-4 py-2 text-xs font-bold text-gray-600 dark:text-gray-300 uppercase tracking-wider"
          style={{ paddingLeft: `${indent + 16}px` }}
        >
          {row.name}
        </td>
      </tr>
    );
  }

  if (row.rowType === 'NET_TOTAL') {
    const isProfit = !row.isNegative;
    return (
      <tr className="border-t-2 border-double border-gray-400 dark:border-gray-500 bg-gray-50 dark:bg-gray-800/50">
        <td
          className="px-4 py-3 text-sm font-bold text-gray-900 dark:text-white"
          style={{ paddingLeft: `${indent + 16}px` }}
        >
          {row.name}
        </td>
        {monthly ? <MonthlyCells row={row} cellHeaders={cellHeaders} pad="px-4 py-3" strong /> : (
          <>
            <td className={cn(
              'px-4 py-3 text-sm text-right tabular-nums font-bold',
              isProfit
                ? 'text-green-700 dark:text-green-400'
                : 'text-red-600 dark:text-red-400'
            )}>
              {row.isNegative ? '-' : ''}{fmtAmt(Math.abs(row.amount))}
            </td>
            {showComparative && (
              <td className={cn(
                'px-4 py-3 text-sm text-right tabular-nums font-bold',
                row.comparativeAmount != null && row.comparativeAmount >= 0
                  ? 'text-green-700 dark:text-green-400'
                  : 'text-red-600 dark:text-red-400'
              )}>
                {row.comparativeAmount != null ? fmtAmt(Math.abs(row.comparativeAmount)) : '—'}
              </td>
            )}
            {showComparative && <VarianceCells row={row} pad="px-4 py-3" bold />}
          </>
        )}
      </tr>
    );
  }

  if (row.rowType === 'SUBTOTAL') {
    return (
      <tr className="border-t border-gray-300 dark:border-gray-600">
        <td
          className="px-4 py-2 text-sm font-semibold text-gray-800 dark:text-gray-200"
          style={{ paddingLeft: `${indent + 16}px` }}
        >
          {row.name}
        </td>
        {monthly ? <MonthlyCells row={row} cellHeaders={cellHeaders} pad="px-4 py-2" strong /> : (
          <>
            <td className="px-4 py-2 text-sm text-right tabular-nums font-semibold text-gray-900 dark:text-white border-t border-gray-300 dark:border-gray-600">
              {row.isNegative ? '-' : ''}{fmtAmt(Math.abs(row.amount))}
            </td>
            {showComparative && (
              <td className="px-4 py-2 text-sm text-right tabular-nums font-semibold text-gray-700 dark:text-gray-300 border-t border-gray-300 dark:border-gray-600">
                {row.comparativeAmount != null ? fmtAmt(Math.abs(row.comparativeAmount)) : '—'}
              </td>
            )}
            {showComparative && <VarianceCells row={row} pad="px-4 py-2" bold />}
          </>
        )}
      </tr>
    );
  }

  if (row.rowType === 'CATEGORY') {
    return (
      <tr className="border-t border-gray-100 dark:border-gray-800">
        <td
          colSpan={colSpanTotal}
          className="px-4 py-1.5 text-sm font-semibold italic text-gray-700 dark:text-gray-200"
          style={{ paddingLeft: `${indent + 16}px` }}
        >
          {row.name}
        </td>
      </tr>
    );
  }

  // ACCOUNT row
  return (
    <tr className="border-t border-gray-50 dark:border-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-800/30 group">
      <td
        className="px-4 py-1.5 text-sm text-gray-700 dark:text-gray-300"
        style={{ paddingLeft: `${indent + 16}px` }}
      >
        <span className="flex items-center gap-1.5">
          {row.code && (
            <span className="font-mono text-xs text-gray-400 dark:text-gray-500">{row.code}</span>
          )}
          {row.name}
          {row.accountId && onDrilldown && (
            <button
              onClick={() => onDrilldown(row.accountId!)}
              className="opacity-0 group-hover:opacity-100 text-brand-500 hover:text-brand-700 ml-1"
              title="View Account Ledger"
            >
              <ExternalLink size={11} />
            </button>
          )}
        </span>
      </td>
      {monthly ? <MonthlyCells row={row} cellHeaders={cellHeaders} pad="px-4 py-1.5" /> : (
        <>
          <td className={cn(
            'px-4 py-1.5 text-sm text-right tabular-nums',
            row.isNegative ? 'text-red-600 dark:text-red-400' : 'text-gray-800 dark:text-gray-200'
          )}>
            {row.isNegative ? '-' : ''}{fmtAmt(Math.abs(row.amount))}
          </td>
          {showComparative && (
            <td className={cn(
              'px-4 py-1.5 text-sm text-right tabular-nums',
              row.comparativeAmount != null && row.comparativeAmount < 0
                ? 'text-red-600 dark:text-red-400'
                : 'text-gray-600 dark:text-gray-400'
            )}>
              {row.comparativeAmount != null ? fmtAmt(Math.abs(row.comparativeAmount)) : '—'}
            </td>
          )}
          {showComparative && <VarianceCells row={row} pad="px-4 py-1.5" />}
        </>
      )}
    </tr>
  );
}

// ── Component ──────────────────────────────────────────────────────────────────

export const ProfitAndLossPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const { user } = useAuth();

  const [filter, setFilter] = useState<FinancialStatementFilter>({
    fromDate: firstOfMonth(),
    toDate: today(),
    showComparative: false,
  });
  const [data, setData] = useState<FinancialStatementDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPrint, setShowPrint] = useState(false);

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await reportApi.getProfitAndLoss(filter);
      setData(res.data?.data ?? null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const drilldown = (accountId: string) => {
    navigate(
      `${AppRoutes.REPORTS_ACCOUNT_LEDGER}?accountId=${accountId}&fromDate=${filter.fromDate ?? ''}&toDate=${filter.toDate}`
    );
  };

  if (!hasPermission(Permissions.RPT_PL_STATEMENT)) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
        You do not have permission to view this report.
      </div>
    );
  }

  // Monthly and comparative are mutually exclusive presentations; the response's own mode is
  // authoritative for rendering (it reflects what was actually generated).
  const monthly = data?.mode === 'MONTHLY';
  const cellHeaders = data?.cellHeaders ?? [];
  const showComparative = !monthly && (filter.showComparative ?? false);
  const monthlyRequested = filter.mode === 'MONTHLY';   // drives the control, before generating
  // Layout width: monthly = name + N periods + total; comparative = name+amount+comp+Δ+Δ%; else name+amount.
  const colSpanTotal = monthly ? cellHeaders.length + 2 : showComparative ? 5 : 2;

  const handleExcel = () => {
    if (!data) return;
    // Monthly export gets a column per period plus a total; otherwise the standard columns.
    const columns = monthly
      ? [
          { key: 'Name', header: 'Description', width: 48 },
          ...cellHeaders.map(h => ({ key: h, header: h, width: 16, numFmt: '#,##0.00' })),
          { key: 'Total', header: 'Total', width: 16, numFmt: '#,##0.00' },
          { key: 'RowType', header: 'Row Type', width: 16 },
        ]
      : EXCEL_COLUMNS;
    const rows = monthly
      ? data.rows.filter(r => r.rowType !== 'SPACER').map(r => ({
          Name: r.name,
          ...Object.fromEntries(cellHeaders.map((h, i) => [h, r.cells?.[i] ?? ''])),
          Total: r.amount,
          RowType: r.rowType,
        }))
      : buildExcelRows(data.rows);
    exportToExcel({
      sheetName: 'Profit & Loss',
      columns,
      rows,
      title: 'Profit & Loss Statement',
      subtitle: `${data.companyName} · ${filter.fromDate ?? ''} to ${filter.toDate}`,
    }, `ProfitAndLoss_${filter.fromDate ?? ''}_${filter.toDate}`);
  };

  const handlePrint = () => setShowPrint(true);

  return (
    <div className="flex flex-col h-full gap-4 p-4">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <TrendingUp size={22} className="text-brand-500" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Profit &amp; Loss Statement</h1>
        </div>
        <ReportToolbar
          onExcelExport={handleExcel}
          onPrint={handlePrint}
          hasData={!!data}
        />
      </div>

      {/* Filter Panel */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
        {/* Row 1 — Dates + quick period */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className={LABEL_CLS}>From Date (Period Start) *</label>
            <input
              type="date"
              value={filter.fromDate ?? ''}
              onChange={e => setFilter(f => ({ ...f, fromDate: e.target.value || undefined }))}
              className={INPUT_CLS}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>To Date (Period End) *</label>
            <input
              type="date"
              value={filter.toDate}
              onChange={e => setFilter(f => ({ ...f, toDate: e.target.value }))}
              className={INPUT_CLS}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>Quick Period</label>
            <FinancialPeriodSelect
              onPeriodChange={(from, to) => {
                setFilter(f => ({ ...f, fromDate: from, toDate: to }));
              }}
            />
          </div>
        </div>

        {/* Comparative inputs (revealed when checkbox is on) */}
        {showComparative && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className={LABEL_CLS}>Comparative From</label>
              <input
                type="date"
                value={filter.comparativeFromDate ?? ''}
                onChange={e => setFilter(f => ({ ...f, comparativeFromDate: e.target.value || undefined }))}
                className={INPUT_CLS}
              />
            </div>
            <div>
              <label className={LABEL_CLS}>Comparative To</label>
              <input
                type="date"
                value={filter.comparativeToDate ?? ''}
                onChange={e => setFilter(f => ({ ...f, comparativeToDate: e.target.value || undefined }))}
                className={INPUT_CLS}
              />
            </div>
            <div>
              {/* Leaving these blank compares this location/cost centre against itself over the two
                  date ranges above — set one to compare a different branch instead, same period by
                  default if the dates above are also left blank (GAP-019). */}
              <label className={LABEL_CLS}>Comparative Location</label>
              <LocationSelect
                value={filter.comparativeLocationId}
                onChange={id => setFilter(f => ({ ...f, comparativeLocationId: id ?? undefined }))}
              />
            </div>
            <div>
              <label className={LABEL_CLS}>Comparative Cost Centre</label>
              <CostCentreSelect
                value={filter.comparativeCostCenterId}
                onChange={id => setFilter(f => ({ ...f, comparativeCostCenterId: id ?? undefined }))}
              />
            </div>
          </div>
        )}

        {/* Row 2 — Location, Cost Centre */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className={LABEL_CLS}>Location</label>
            <LocationSelect
              value={filter.locationId}
              onChange={id => setFilter(f => ({ ...f, locationId: id ?? undefined }))}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>Cost Centre</label>
            <CostCentreSelect
              value={filter.costCenterId}
              onChange={id => setFilter(f => ({ ...f, costCenterId: id ?? undefined }))}
            />
          </div>
        </div>

        {/* Checkbox + Run */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-5">
            <label className={cn('flex items-center gap-2 text-sm cursor-pointer',
              monthlyRequested ? 'text-gray-400 dark:text-gray-600' : 'text-gray-700 dark:text-gray-300')}>
              <input
                type="checkbox"
                checked={filter.showComparative ?? false}
                disabled={monthlyRequested}
                onChange={e => setFilter(f => ({ ...f, showComparative: e.target.checked, mode: undefined }))}
                className="rounded"
              />
              Show Comparative Period
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={monthlyRequested}
                onChange={e => setFilter(f => ({
                  ...f,
                  mode: e.target.checked ? 'MONTHLY' : undefined,
                  showComparative: e.target.checked ? false : f.showComparative,
                }))}
                className="rounded"
              />
              Monthly breakdown
            </label>
          </div>
          <RunReportButton
            label="Generate Report"
            onClick={run}
            isLoading={loading}
            disabled={!filter.toDate}
          />
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && !data && (
        <div className="flex-1 flex items-center justify-center">
          <Spinner size="lg" />
        </div>
      )}

      {/* Results */}
      {data && (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 flex flex-col min-h-0 flex-1">
          {/* Report header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <div>
              <div className="text-sm font-semibold text-gray-900 dark:text-white">{data.companyName}</div>
              <div className="text-base font-bold text-gray-900 dark:text-white mt-0.5">{data.title}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {data.fromDate ? `${fmtDate(data.fromDate)} to ${fmtDate(data.toDate)}` : `As at ${fmtDate(data.toDate)}`}
                {' · '}Generated {fmtDate(data.generatedAt)}
              </div>
            </div>
            {data.validationMessage && (
              <div className={cn(
                'flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium',
                data.isBalanced
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                  : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
              )}>
                {data.isBalanced ? <CheckCircle2 size={12} /> : <AlertCircle size={12} />}
                {data.validationMessage}
              </div>
            )}
          </div>

          {/* Print area */}
          <div data-print-section id="report-print-area" className="overflow-auto flex-1">
            {/* Print-only header */}
            <div className="hidden print:block px-4 pt-4 pb-2 text-center">
              <div className="text-lg font-bold">{data.companyName}</div>
              <div className="text-base font-semibold mt-1">Profit &amp; Loss Statement</div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                For the period {fmtDate(data.fromDate)} to {fmtDate(data.toDate)}
              </div>
            </div>

            <table className="w-full text-sm border-collapse">
              <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800 z-10">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Description</th>
                  {monthly ? (
                    <>
                      {cellHeaders.map(h => (
                        <th key={h} className="px-4 py-2 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide w-28">
                          {h}
                        </th>
                      ))}
                      <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide w-32">
                        Total
                      </th>
                    </>
                  ) : (
                    <>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide w-36">
                        {data.fromDate
                          ? `${fmtDate(data.fromDate)} – ${fmtDate(data.toDate)}`
                          : fmtDate(data.toDate)}
                      </th>
                      {showComparative && (
                        <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide w-36">
                          Comparative
                        </th>
                      )}
                      {showComparative && (
                        <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide w-28">
                          Variance
                        </th>
                      )}
                      {showComparative && (
                        <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide w-20">
                          Δ %
                        </th>
                      )}
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row, idx) => (
                  <FsRow
                    key={`${row.rowType}-${row.code ?? idx}-${idx}`}
                    row={row}
                    showComparative={showComparative}
                    monthly={monthly}
                    cellHeaders={cellHeaders}
                    colSpanTotal={colSpanTotal}
                    onDrilldown={drilldown}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!data && !loading && (
        <div className="flex-1 flex items-center justify-center text-gray-400 dark:text-gray-600 text-sm">
          Set the period dates and click "Generate Report" to view the Profit &amp; Loss Statement.
        </div>
      )}

      {/* ── Print Preview ─────────────────────────────────────────────────── */}
      {data && (
        <ReportPrintPreview
          isOpen={showPrint}
          onClose={() => setShowPrint(false)}
          title="Profit & Loss Statement"
          orientation="portrait"
        >
          <ReportHeader
            reportTitle={data.title ?? 'Profit & Loss Statement'}
            printedBy={user?.fullName}
            printedAt={new Date()}
            meta={[
              { label: 'Company', value: data.companyName },
              { label: 'Period',  value: data.fromDate ? `${fmtDate(data.fromDate)} to ${fmtDate(data.toDate)}` : `As at ${fmtDate(data.toDate)}` },
            ]}
          />
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9px', marginTop: '8px' }}>
            <thead>
              <tr>
                <th style={{ background: '#1e3a5f', color: 'white', padding: '5px 8px', textAlign: 'left', fontWeight: 700, fontSize: '9px' }}>Description</th>
                <th style={{ background: '#1e3a5f', color: 'white', padding: '5px 8px', textAlign: 'right', fontWeight: 700, fontSize: '9px', width: '120px' }}>
                  {data.fromDate ? `${fmtDate(data.fromDate)} – ${fmtDate(data.toDate)}` : fmtDate(data.toDate)}
                </th>
                {showComparative && (
                  <th style={{ background: '#1e3a5f', color: 'white', padding: '5px 8px', textAlign: 'right', fontWeight: 700, fontSize: '9px', width: '120px' }}>Comparative</th>
                )}
              </tr>
            </thead>
            <tbody>
              {data.rows.filter((r: FinancialStatementRow) => r.rowType !== 'SPACER').map((row: FinancialStatementRow, idx: number) => {
                const indent = (row.indentLevel ?? 0) * 12;
                const isHeader   = row.rowType === 'SECTION_HEADER';
                const isCategory = row.rowType === 'CATEGORY';
                const isSubtotal = row.rowType === 'SUBTOTAL';
                const isNetTotal = row.rowType === 'NET_TOTAL';
                const isAccount  = row.rowType === 'ACCOUNT';
                return (
                  <tr key={idx} style={{
                    borderBottom: '1px solid #f3f4f6',
                    background: isHeader ? '#e8edf5' : isNetTotal ? '#f9fafb' : undefined,
                    borderTop: isNetTotal ? '2px solid #374151' : isSubtotal ? '1px solid #d1d5db' : undefined,
                  }}>
                    <td style={{
                      padding: `${isHeader || isNetTotal ? 5 : 3}px 8px`,
                      paddingLeft: `${indent + 8}px`,
                      fontWeight: isHeader || isNetTotal || isSubtotal ? 700 : 400,
                      fontStyle: isCategory ? 'italic' : 'normal',
                      fontSize: isHeader ? '8.5px' : '9px',
                      color: isHeader ? '#1e3a5f' : isAccount ? '#374151' : '#111827',
                      letterSpacing: isHeader ? '0.05em' : undefined,
                      textTransform: isHeader ? 'uppercase' : undefined,
                    }}>
                      {row.code && isAccount && (
                        <span style={{ fontFamily: 'monospace', color: '#9ca3af', marginRight: '6px', fontSize: '8px' }}>{row.code}</span>
                      )}
                      {row.name}
                    </td>
                    {(isAccount || isSubtotal || isNetTotal) ? (
                      <td style={{ padding: '3px 8px', textAlign: 'right', fontFamily: 'monospace', fontWeight: isNetTotal || isSubtotal ? 700 : 400, color: isNetTotal && !row.isNegative ? '#16a34a' : isNetTotal && row.isNegative ? '#dc2626' : '#111827', fontSize: '9px' }}>
                        {row.isNegative ? '-' : ''}{fmtAmt(Math.abs(row.amount))}
                      </td>
                    ) : (
                      <td />
                    )}
                    {showComparative && (
                      (isAccount || isSubtotal || isNetTotal) ? (
                        <td style={{ padding: '3px 8px', textAlign: 'right', fontFamily: 'monospace', fontWeight: isNetTotal || isSubtotal ? 700 : 400, color: '#6b7280', fontSize: '9px' }}>
                          {row.comparativeAmount != null ? `${fmtAmt(Math.abs(row.comparativeAmount))}` : '—'}
                        </td>
                      ) : <td />
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          <ReportFooter />
        </ReportPrintPreview>
      )}
    </div>
  );
};
